/**
 * Poker Coach - AI-powered suggestions and odds calculation for Texas Hold'em
 */

import { bestFiveCardHand } from './pokerEngine.js';

// Hand strength rankings (0-9)
const HAND_RANKINGS = {
  'High Card': 0,
  'Pair': 1,        // Match HOLDEM_HANDS.PAIR.name from pokerConstants.js
  'Two Pair': 2,
  'Three of a Kind': 3,
  'Straight': 4,
  'Flush': 5,
  'Full House': 6,
  'Four of a Kind': 7,
  'Straight Flush': 8,
  'Royal Flush': 9
};

/**
 * Evaluate current hand from hole cards and community cards
 * Returns the evaluated hand or null if not enough cards
 */
const evaluateCurrentHand = (holeCards, communityCards) => {
  if (!holeCards || holeCards.length < 2) return null;
  
  const allCards = [...holeCards, ...(communityCards || [])];
  if (allCards.length < 5) return null;
  
  try {
    const best = bestFiveCardHand(allCards);
    return best?.evaluation || null;
  } catch (e) {
    return null;
  }
};

// Starting hand rankings for Texas Hold'em (simplified Chen formula)
const getStartingHandStrength = (card1, card2) => {
  if (!card1 || !card2) return 0;
  
  const rank1 = card1.rank;
  const rank2 = card2.rank;
  const suited = card1.suit === card2.suit;
  const highCard = Math.max(rank1, rank2);
  const lowCard = Math.min(rank1, rank2);
  const gap = highCard - lowCard;
  const isPair = rank1 === rank2;
  
  let score = 0;
  
  // Base score from high card
  if (highCard === 14) score = 10; // Ace
  else if (highCard === 13) score = 8; // King
  else if (highCard === 12) score = 7; // Queen
  else if (highCard === 11) score = 6; // Jack
  else score = highCard / 2;
  
  // Pair bonus
  if (isPair) {
    score *= 2;
    if (score < 5) score = 5;
  }
  
  // Suited bonus
  if (suited) score += 2;
  
  // Gap penalty
  if (gap === 1) score -= 0;
  else if (gap === 2) score -= 1;
  else if (gap === 3) score -= 2;
  else if (gap === 4) score -= 4;
  else if (gap >= 5) score -= 5;
  
  // Straight potential bonus
  if (gap <= 2 && lowCard >= 10) score += 1;
  
  return Math.max(0, Math.min(20, score));
};

// Calculate pot odds
export const calculatePotOdds = (potSize, toCall) => {
  if (toCall <= 0) return { odds: Infinity, percentage: 100 };
  const odds = potSize / toCall;
  const percentage = (toCall / (potSize + toCall)) * 100;
  return { odds: odds.toFixed(1), percentage: percentage.toFixed(1) };
};

// Estimate outs (simplified)
const estimateOuts = (holeCards, communityCards, currentHand) => {
  if (!currentHand || !holeCards || holeCards.length < 2) return { outs: 0, draws: [] };
  
  const allCards = [...holeCards, ...(communityCards || [])];
  const suits = {};
  const ranks = {};
  
  allCards.forEach(card => {
    suits[card.suit] = (suits[card.suit] || 0) + 1;
    ranks[card.rank] = (ranks[card.rank] || 0) + 1;
  });
  
  const draws = [];
  let totalOuts = 0;
  
  // Flush draw (4 to a flush)
  Object.entries(suits).forEach(([suit, count]) => {
    if (count === 4) {
      draws.push({ type: 'Flush Draw', outs: 9, suit });
      totalOuts += 9;
    }
  });
  
  // Straight draw (simplified - check for 4 consecutive)
  const sortedRanks = [...new Set(allCards.map(c => c.rank))].sort((a, b) => a - b);
  for (let i = 0; i <= sortedRanks.length - 4; i++) {
    const consecutive = sortedRanks.slice(i, i + 4);
    if (consecutive[3] - consecutive[0] === 3) {
      // Open-ended straight draw
      draws.push({ type: 'Straight Draw', outs: 8 });
      totalOuts += 8;
      break;
    } else if (consecutive[3] - consecutive[0] === 4 && consecutive.length === 4) {
      // Gutshot straight draw
      draws.push({ type: 'Gutshot', outs: 4 });
      totalOuts += 4;
      break;
    }
  }
  
  // Pair to trips/two pair
  const pairCount = Object.values(ranks).filter(c => c === 2).length;
  if (pairCount === 1 && communityCards && communityCards.length < 5) {
    draws.push({ type: 'Set/Trips', outs: 2 });
    totalOuts += 2;
  }
  
  // Overcards (if no pair yet)
  if (currentHand.hand.name === 'High Card' && communityCards && communityCards.length >= 3) {
    const boardHighest = Math.max(...communityCards.map(c => c.rank));
    const overCards = holeCards.filter(c => c.rank > boardHighest).length;
    if (overCards > 0) {
      draws.push({ type: 'Overcards', outs: overCards * 3 });
      totalOuts += overCards * 3;
    }
  }
  
  return { outs: Math.min(totalOuts, 20), draws };
};

// Calculate win probability estimate (improved algorithm)
export const calculateWinProbability = (holeCards, communityCards, currentHand, numOpponents) => {
  if (!holeCards || holeCards.length < 2) return 0;
  
  const startingStrength = getStartingHandStrength(holeCards[0], holeCards[1]);
  const handRank = currentHand ? HAND_RANKINGS[currentHand.hand?.name] || 0 : 0;
  const { outs } = estimateOuts(holeCards, communityCards, currentHand);
  const cardsDealt = (communityCards?.length || 0);
  
  // Base probability from current hand strength
  let baseProbability;
  if (cardsDealt >= 3) {
    // Post-flop: use realistic equity estimates based on hand strength
    // These are approximate equity vs random hands
    const handEquities = {
      0: 30,  // High Card - ~30% vs multiple opponents
      1: 50,  // Pair - ~50% (top pair is strong)
      2: 65,  // Two Pair - ~65%
      3: 75,  // Three of a Kind - ~75%
      4: 82,  // Straight - ~82%
      5: 85,  // Flush - ~85%
      6: 90,  // Full House - ~90%
      7: 95,  // Four of a Kind - ~95%
      8: 98,  // Straight Flush - ~98%
      9: 99   // Royal Flush - ~99%
    };
    baseProbability = handEquities[handRank] || 30;
    
    // Bonus for top pair (check if our pair uses our highest hole card)
    if (handRank === 1 && communityCards && communityCards.length >= 3) {
      const boardHighest = Math.max(...communityCards.map(c => c.rank));
      const ourHighest = Math.max(holeCards[0].rank, holeCards[1].rank);
      if (ourHighest >= boardHighest) {
        baseProbability += 10; // Top pair bonus
      }
    }
  } else {
    // Pre-flop: use starting hand strength
    baseProbability = 20 + (startingStrength * 2.5); // Max ~70% for premium hands
  }
  
  // Adjust for outs (potential to improve)
  const cardsTocome = Math.max(0, 5 - cardsDealt);
  if (cardsTocome > 0 && outs > 0 && handRank < 4) {
    // Only count draws if we don't already have a strong hand
    const improvementChance = Math.min(outs * 2 * cardsTocome, 30);
    baseProbability += improvementChance * 0.3; // Weight at 30%
  }
  
  // Adjust for number of opponents (diminishing penalty for strong hands)
  const effectiveOpponents = Math.min(numOpponents, 5);
  let opponentPenalty;
  if (handRank >= 5) {
    opponentPenalty = 1; // Strong hands barely affected
  } else if (handRank >= 3) {
    opponentPenalty = 2;
  } else if (handRank >= 1) {
    opponentPenalty = 3; // Pairs - moderate penalty
  } else {
    opponentPenalty = 4; // High card - higher penalty
  }
  baseProbability -= (effectiveOpponents - 1) * opponentPenalty;
  
  return Math.max(10, Math.min(95, Math.round(baseProbability)));
};

// Get coach recommendation
export const getCoachRecommendation = (state) => {
  const { 
    holeCards, communityCards, currentHand, pot, currentBet, 
    playerChips, playerCurrentBet, position, numActivePlayers, phase
  } = state;
  
  if (!holeCards || holeCards.length < 2) {
    return { action: 'wait', reason: 'Waiting for cards...', confidence: 0 };
  }
  
  // Evaluate the hand if not already evaluated (happens during betting rounds)
  const evaluatedHand = currentHand || evaluateCurrentHand(holeCards, communityCards);
  
  const toCall = currentBet - playerCurrentBet;
  const potOdds = calculatePotOdds(pot, toCall);
  const startingStrength = getStartingHandStrength(holeCards[0], holeCards[1]);
  const handStrength = evaluatedHand ? HAND_RANKINGS[evaluatedHand.hand?.name] || 0 : 0;
  const winProb = calculateWinProbability(holeCards, communityCards, evaluatedHand, numActivePlayers);
  const { outs, draws } = estimateOuts(holeCards, communityCards, evaluatedHand);
  
  let action = 'fold';
  let reason = '';
  let confidence = 50;
  
  // Pre-flop logic
  if (phase === 'preflop') {
    if (startingStrength >= 16) {
      action = 'raise';
      reason = 'Premium hand - raise for value';
      confidence = 90;
    } else if (startingStrength >= 12) {
      action = toCall <= pot * 0.1 ? 'call' : 'raise';
      reason = 'Strong hand - play aggressively';
      confidence = 75;
    } else if (startingStrength >= 8) {
      action = toCall <= pot * 0.2 ? 'call' : 'fold';
      reason = 'Playable hand - call if cheap';
      confidence = 60;
    } else if (startingStrength >= 5 && position >= 4) {
      action = toCall === 0 ? 'check' : 'fold';
      reason = 'Marginal hand - only play in position';
      confidence = 50;
    } else {
      action = toCall === 0 ? 'check' : 'fold';
      reason = 'Weak hand - fold to aggression';
      confidence = 70;
    }
  } 
  // Post-flop logic
  else {
    const hasDraws = draws.length > 0;
    const handName = evaluatedHand?.hand?.name || 'High Card';
    
    if (handStrength >= 5) { // Flush or better
      action = 'raise';
      reason = `Strong made hand (${handName}) - bet for value`;
      confidence = 90;
    } else if (handStrength >= 4) { // Straight
      action = toCall <= pot * 0.5 ? 'raise' : 'call';
      reason = `Great hand (${handName}) - build the pot`;
      confidence = 85;
    } else if (handStrength >= 3) { // Trips or better
      action = toCall <= pot * 0.5 ? 'raise' : 'call';
      reason = `Good hand (${handName}) - build the pot`;
      confidence = 80;
    } else if (handStrength >= 2) { // Two Pair
      if (winProb >= 50 || toCall <= pot * 0.3) {
        action = toCall === 0 ? 'raise' : 'call';
        reason = `Solid hand (${handName}) - ${toCall === 0 ? 'bet for value' : 'call with good equity'}`;
        confidence = 70;
      } else {
        action = 'call';
        reason = `${handName} - be cautious, check the board texture`;
        confidence = 55;
      }
    } else if (handStrength >= 1) { // Pair
      // Check if it's top pair by comparing with board
      let isTopPair = false;
      if (communityCards && communityCards.length >= 3) {
        const boardHighest = Math.max(...communityCards.map(c => c.rank));
        const ourPairRank = evaluatedHand?.highCards?.[0] || 0;
        isTopPair = ourPairRank >= boardHighest;
      }
      
      if (isTopPair && winProb >= 45) {
        action = toCall === 0 ? 'raise' : (toCall <= pot * 0.5 ? 'call' : 'raise');
        reason = `Top ${handName} - ${toCall === 0 ? 'bet for value' : 'strong enough to continue'}`;
        confidence = 70;
      } else if (winProb >= 40 || toCall === 0) {
        action = toCall === 0 ? 'check' : 'call';
        reason = `${handName} - ${toCall === 0 ? 'check to control pot' : 'call with decent equity'}`;
        confidence = 60;
      } else {
        action = 'fold';
        reason = `Weak ${handName} facing aggression - fold`;
        confidence = 55;
      }
    } else if (hasDraws && outs >= 8) {
      // Drawing hand with good odds
      const neededOdds = (100 / (outs * 2)); // Rule of 2
      if (parseFloat(potOdds.percentage) <= neededOdds * 1.5) {
        action = 'call';
        reason = `${draws[0].type} with ${outs} outs - good pot odds to draw`;
        confidence = 65;
      } else {
        action = 'fold';
        reason = `${draws[0].type} but pot odds not favorable`;
        confidence = 50;
      }
    } else if (toCall === 0) {
      action = 'check';
      reason = `${handName} - check and see what develops`;
      confidence = 70;
    } else {
      action = 'fold';
      reason = `${handName} with no draws - save your chips`;
      confidence = 75;
    }
  }
  
  return { action, reason, confidence, winProb, outs, draws, potOdds, startingStrength };
};

// Get contextual tips based on game phase
export const getContextualTips = (phase, situation) => {
  const tips = {
    preflop: [
      "Position matters! Play tighter from early position, looser from late position.",
      "Premium hands (AA, KK, QQ, AK) should almost always be raised.",
      "Suited connectors (like 7♠8♠) have good implied odds for hitting straights/flushes.",
      "Don't fall in love with Ace-rag hands - they often make weak pairs."
    ],
    flop: [
      "You'll connect with the flop about 1/3 of the time.",
      "Count your outs - each out gives ~2% chance to improve per card to come.",
      "Top pair with top kicker is a strong hand in most situations.",
      "Be wary of wet boards (connected, suited) - opponents may have draws."
    ],
    turn: [
      "The turn is where hand values crystallize - reassess your holdings.",
      "If you were drawing and missed, consider giving up without proper odds.",
      "Pot control becomes important - don't bloat pots with marginal hands."
    ],
    river: [
      "All cards are out - you either have the best hand or you don't.",
      "Value bet when you think you're ahead; check/fold when behind.",
      "Bluffs on the river should tell a consistent story."
    ],
    showdown: [
      "Review the hand - did you play it optimally?",
      "Note opponents' playing styles for future hands."
    ]
  };
  
  return tips[phase] || tips.preflop;
};

// Learning mode explanations
export const getLearningExplanation = (phase) => {
  const explanations = {
    waiting: {
      title: "Getting Ready",
      content: "You'll receive 2 private cards (hole cards). Your goal is to make the best 5-card hand using your 2 cards and 5 community cards."
    },
    preflop: {
      title: "Pre-Flop",
      content: "You have your 2 hole cards. Decide whether to fold, call, or raise based on hand strength and position. Blinds force action."
    },
    flop: {
      title: "The Flop",
      content: "3 community cards are dealt. Now you can see 5 of 7 possible cards. Assess your hand strength and potential draws."
    },
    turn: {
      title: "The Turn",
      content: "4th community card revealed. With 6 cards known, hand values become clearer. This is often where big bets happen."
    },
    river: {
      title: "The River", 
      content: "Final community card! All 7 cards are known. Make your final betting decision - there's no improving from here."
    },
    showdown: {
      title: "Showdown",
      content: "Remaining players reveal their hands. Best 5-card combination wins the pot!"
    }
  };
  
  return explanations[phase] || explanations.waiting;
};

export { getStartingHandStrength, estimateOuts, HAND_RANKINGS };
